import { type EmergencyCreditStatsCommon } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";

export interface CreditDisplayProps {
  completedCount: number;
  requiredCount: number;
  isCreditAvailableWithNoProgress: boolean;
  /**
   * Helper text shown below the progress bar, e.g. "5/20 shifts complete".
   * Undefined when the bar is full (credit available with no progress).
   */
  subtext: string | undefined;
}

interface CreditDisplayPropsOptions {
  /**
   * When true, treats the bar as full (credit was just earned).
   * Takes priority over other logic.
   */
  gainedCredit?: boolean;
  /**
   * Override the internal "has credit available" check (totalAvailableCredits > 0).
   * Useful when a credit was used in the current session but totalAvailableCredits
   * has already been decremented.
   */
  hasCredit?: boolean;
}

/**
 * Derives display props from credit stats.
 *
 * When a credit is available but the worker has no progress toward the next one,
 * the bar should show as full and helper text should be hidden.
 */
export function getCreditDisplayProps(
  stats: Pick<
    EmergencyCreditStatsCommon,
    "totalAvailableCredits" | "completedShiftsTowardsNextCredit" | "requiredShiftsForNextCredit"
  >,
  options?: CreditDisplayPropsOptions
): CreditDisplayProps {
  const { gainedCredit = false, hasCredit: hasCreditOverride } = options ?? {};

  if (gainedCredit) {
    return {
      completedCount: stats.requiredShiftsForNextCredit,
      requiredCount: stats.requiredShiftsForNextCredit,
      isCreditAvailableWithNoProgress: true,
      subtext: undefined,
    };
  }

  const hasCredit = hasCreditOverride ?? stats.totalAvailableCredits > 0;
  const isCreditAvailableWithNoProgress = hasCredit && stats.completedShiftsTowardsNextCredit === 0;

  const completedCount = isCreditAvailableWithNoProgress
    ? stats.requiredShiftsForNextCredit
    : stats.completedShiftsTowardsNextCredit;

  const subtext = isCreditAvailableWithNoProgress
    ? undefined
    : `${stats.completedShiftsTowardsNextCredit}/${stats.requiredShiftsForNextCredit} shifts complete`;

  return {
    completedCount,
    requiredCount: stats.requiredShiftsForNextCredit,
    isCreditAvailableWithNoProgress,
    subtext,
  };
}
